import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Turnstile from './Turnstile';
import '../styles/Auth.css';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [requires2fa, setRequires2fa] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [verificationToken, setVerificationToken] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (requires2fa && verificationToken) {
        // Step 2: Verify with stored token + TOTP (skip /auth/login entirely)
        await login(email, password, totpCode, null, verificationToken);
        navigate('/chat');
      } else {
        // Step 1: Initial login with Turnstile
        const result = await login(email, password, null, turnstileToken);
        if (result.requires_2fa) {
          setVerificationToken(result.verification_token);
          setRequires2fa(true);
          setLoading(false);
          return;
        }
        navigate('/chat');
      }
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setRequires2fa(false);
    setVerificationToken(null);
    setTotpCode('');
    setError('');
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <h1 className="auth-title">
            {requires2fa ? 'Two-Factor Authentication' : 'Welcome back'}
          </h1>
          <p className="auth-subtitle">
            {requires2fa 
              ? 'Enter the code from your authenticator app'
              : 'Sign in to continue to your projects'
            }
          </p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && (
            <div className="auth-error">
              {error}
            </div>
          )}

          {!requires2fa ? (
            <>
              <div className="form-group">
                <label htmlFor="email" className="form-label">Email</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="form-input"
                  placeholder="you@example.com"
                  required
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label htmlFor="password" className="form-label">Password</label>
                <input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="form-input"
                  placeholder="••••••••"
                  required
                />
              </div>
              <Turnstile onVerify={setTurnstileToken} />
            </>
          ) : (
            <div className="form-group">
              <label htmlFor="totp" className="form-label">Authentication Code</label>
              <input
                id="totp"
                type="text"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="form-input form-input-2fa"
                placeholder="000000"
                required
                autoFocus
                maxLength={6}
                autoComplete="one-time-code"
              />
              <p className="form-hint-2fa">
                Enter 6-digit code or a backup code
              </p>
            </div>
          )}

          <button 
            type="submit" 
            className="btn btn-primary btn-full"
            disabled={loading || (!requires2fa && !turnstileToken) || (requires2fa && totpCode.length < 6)}
          >
            {loading ? 'Signing in...' : requires2fa ? 'Verify' : 'Sign In'}
          </button>
          
          {requires2fa && (
            <button 
              type="button"
              className="btn btn-secondary btn-full"
              onClick={handleBack}
              style={{ marginTop: '12px' }}
            >
              ← Back
            </button>
          )}
        </form>

        {!requires2fa && (
          <p className="auth-switch">
            Don't have an account?{' '}
            <Link to="/signup" className="auth-link">Sign up</Link>
          </p>
        )}
      </div>
    </div>
  );
}

export default Login;
